// Named colour tokens for the Market Probabilities board.
//
// The ladder card mixes Diletta's platform chrome (dilettaPanelSx, DILETTA_*
// from components/v2/widgets/exotics-shared) with a board-specific palette: a
// muted grey TEXT ramp for the dense numeric table, a hairline BORDER/SCROLLBAR
// family, the prob-band heat ramp, and a handful of accent colours. Those
// board-specific values were previously inlined as raw hex/rgba repeated across
// the card -- the recurring standing MINOR. They are named here ONCE so the
// table reads against semantic tokens and a palette tweak is a single edit.
// Platform chrome stays in exotics-shared; this is the board's own layer.
import { ProbBand, FreshnessBand } from './ladder';

// TEXT ramp, brightest -> faintest. The dense ladder table leans on a tight
// grey scale: PRIMARY for the headline figures (rung level, tile value),
// MUTED/FAINT/DIM for the reference column + labels + settled rows, and
// DISABLED/GHOST for the muted em-dash a never-priced cell renders.
export const TEXT_HEADING = '#eaf4fb'; // page-head title (brightest)
export const TEXT_PRIMARY = '#cfe0ee'; // headline figures (level, tile value)
export const TEXT_SUBTLE = '#8aa0b4'; // vs-Spot value
export const TEXT_MUTED = '#7d93a8'; // column heads, BS value, tile/freshness label
export const TEXT_DIM = '#6f8499'; // spread annotation, HIT LV/level text
export const TEXT_FAINT = '#5f7589'; // HIT marker, HIT-BS value, Imm (BS) head
export const TEXT_DISABLED = '#5a6f81'; // caption, LV em-dash, muted tile value
export const TEXT_GHOST = '#4d6072'; // deepest em-dash (BS null, freshness null)

// Hairline border / scrollbar family -- one slate hue at two alphas. SUBTLE is
// the row separator + footer dividers; SCROLLBAR is the slightly stronger
// thumb so the deep ladder reads as scrollable without a heavy native bar.
export const BORDER_SUBTLE = 'rgba(51,87,110,.30)';
export const SCROLLBAR_THUMB = 'rgba(51,87,110,.55)';

// Tile background for the OHLC+Latest / footer tile rows.
export const TILE_BG = '#11212e';

// Spot-marker row accent + its translucent fill, and the banner ccy / locate
// accent + its hover fill. Kept here so the board's accents are named alongside
// the greys rather than re-inlined per use.
export const SPOT_ACCENT = '#2ea8e0';
export const SPOT_ROW_FILL = 'rgba(46,168,224,.12)';
export const ACCENT_SOFT = '#7fb9d8'; // banner ccy + locate button
export const ACCENT_SOFT_HOVER_FILL = 'rgba(127,185,216,.12)';

// Prob-band heat ramp: the LV cell's chip + (where used) text colour, mapped
// from probBand(). Teal -> amber -> orange -> red, mirroring the mock.
export const BAND_COLOUR: Record<ProbBand, string> = {
  low: '#0a7ea4',
  mid: '#FAA638',
  high: '#F3733F',
  extreme: '#ff6b6b',
};

// Freshness-dot colour by band. Reuses the BAND_COLOUR chrome where it fits --
// amber (mid) for stale, red (extreme) for expired -- and a clean green for
// fresh. The green is a ONE-OFF: the BAND_COLOUR family has no green, its `low`
// is a teal that doesn't read as "OK" next to the amber/red.
export const FRESH_GREEN = '#3fb950';
export const FRESH_DOT_COLOUR: Record<FreshnessBand, string> = {
  fresh: FRESH_GREEN,
  stale: BAND_COLOUR.mid,
  expired: BAND_COLOUR.extreme,
};
